import React, { useState } from "react";
import { api } from "../../api/client";
import { Card } from "../../components/common/Card";

const REPORTS = [
  { key: "breakdown", label: "Breakdown Report" },
  { key: "pm-completion", label: "PM Completion Report" },
  { key: "work-orders", label: "Work Order Report" },
  { key: "spare-part-consumption", label: "Spare Part Consumption" },
  { key: "downtime-analysis", label: "Downtime Analysis" },
  { key: "technician-performance", label: "Technician Performance" },
  { key: "maintenance-cost", label: "Maintenance Cost Report" },
];

type ReportRow = Record<string, string | number | null>;

export default function Reports() {
  const [reportKey, setReportKey] = useState(REPORTS[0].key);
  const [rows, setRows] = useState<ReportRow[]>([]);
  const [from, setFrom] = useState(new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10));
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<ReportRow[]>(`/api/reports/${reportKey}?from=${from}&to=${to}`);
      setRows(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to run report");
    } finally {
      setLoading(false);
    }
  };

  const columns = rows.length > 0 ? Object.keys(rows[0]) : [];

  const exportCsv = () => {
    if (rows.length === 0) return;
    const header = columns.join(",");
    const body = rows.map((r) => columns.map((c) => JSON.stringify(r[c] ?? "")).join(",")).join("\n");
    downloadFile(`${header}\n${body}`, `${reportKey}.csv`, "text/csv");
  };

  const exportExcel = () => {
    // Simple Excel-compatible export via HTML table (opens natively in Excel) —
    // for a fully formatted .xlsx, wire this button to the xlsx skill's output.
    if (rows.length === 0) return;
    const html = `<table><tr>${columns.map((c) => `<th>${c}</th>`).join("")}</tr>${rows
      .map((r) => `<tr>${columns.map((c) => `<td>${r[c] ?? ""}</td>`).join("")}</tr>`)
      .join("")}</table>`;
    downloadFile(html, `${reportKey}.xls`, "application/vnd.ms-excel");
  };

  const exportPdf = () => {
    // Opens the browser print dialog scoped to the report table; user selects "Save as PDF".
    window.print();
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold">Reports & Analytics</h1>
        <p className="text-sm text-slate-500">Breakdown, PM completion, cost, downtime, and technician performance reports.</p>
      </div>

      <Card>
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Report</label>
            <select className="input-field" value={reportKey} onChange={(e) => setReportKey(e.target.value)}>
              {REPORTS.map((r) => (
                <option key={r.key} value={r.key}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">From</label>
            <input type="date" className="input-field" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">To</label>
            <input type="date" className="input-field" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <button className="btn-primary" onClick={runReport}>
            {loading ? "Running…" : "Run Report"}
          </button>
          {rows.length > 0 && (
            <div className="ml-auto flex gap-2">
              <button className="btn-secondary" onClick={exportCsv}>CSV</button>
              <button className="btn-secondary" onClick={exportExcel}>Excel</button>
              <button className="btn-secondary" onClick={exportPdf}>PDF</button>
            </div>
          )}
        </div>

        {error && <p className="mb-3 text-sm text-red-600">{error}</p>}

        {rows.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 dark:divide-industrial-700 text-sm">
              <thead>
                <tr>
                  {columns.map((c) => (
                    <th key={c} className="px-3 py-2 text-left font-semibold text-slate-500 dark:text-slate-400 whitespace-nowrap">
                      {c.replace(/_/g, " ")}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-industrial-700">
                {rows.map((row, i) => (
                  <tr key={i}>
                    {columns.map((c) => (
                      <td key={c} className="px-3 py-2 whitespace-nowrap">
                        {String(row[c] ?? "-")}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-slate-400">Run a report to see results.</p>
        )}
      </Card>
    </div>
  );
}

function downloadFile(content: string, filename: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
